import React, { useState, useEffect } from 'react';

const DashboardAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const [analytics, setAnalytics] = useState({});
  const [adminNotes, setAdminNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: '', description: '', status: 'todo', priority: 'medium', category: 'other', assignedTo: '', dueDate: '', dueTime: '' });
  const [noteFilter, setNoteFilter] = useState({ status: '', priority: '', category: '' });
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => { const d = new Date(); d.setDate(d.getDate() - d.getDay() + 1); d.setHours(0,0,0,0); return d; });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [showCompletedNotes, setShowCompletedNotes] = useState(false);

  // Load data on mount
  const loadData = async () => {
    try {
      const [analyticsRes, notesRes] = await Promise.all([
        fetch(`${API}/admin/analytics`, { headers: authHeaders }),
        fetch(`${API}/admin/notes`, { headers: authHeaders })
      ]);
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }
      if (notesRes.ok) {
        const notesData = await notesRes.json();
        setAdminNotes(notesData.notes || []);
      }
    } catch (e) { console.error('Failed to load dashboard data:', e); }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Notes CRUD
  const fetchNotes = async () => {
    try {
      const res = await fetch(`${API}/admin/notes`, { headers: authHeaders });
      if (res.ok) { const data = await res.json(); setAdminNotes(data.notes || []); }
    } catch (e) { console.error('Fetch notes error:', e); }
  };

  const saveNote = async () => {
    if (!noteForm.title.trim()) { showNotification('Title is required', 'error'); return; }
    try {
      const url = editingNote ? `${API}/admin/notes/${editingNote}` : `${API}/admin/notes`;
      const method = editingNote ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, headers: authHeaders,
        body: JSON.stringify(noteForm)
      });
      if (res.ok) {
        showNotification(editingNote ? 'Task updated' : 'Task created', 'success');
        setShowAddNote(false); setEditingNote(null);
        setNoteForm({ title: '', description: '', status: 'todo', priority: 'medium', category: 'other', assignedTo: '', dueDate: '', dueTime: '' });
        fetchNotes();
      }
    } catch (e) { showNotification('Failed to save task', 'error'); }
  };

  const toggleNoteStatus = async (noteId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'todo' : 'done';
    try {
      const res = await fetch(`${API}/admin/notes/${noteId}/status`, {
        method: 'PATCH', headers: authHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) fetchNotes();
    } catch (e) { showNotification('Failed to update status', 'error'); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/admin/notes/${noteId}`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { showNotification('Task deleted', 'success'); fetchNotes(); }
    } catch (e) { showNotification('Failed to delete', 'error'); }
  };

  const startEditNote = (note) => {
    setEditingNote(note.id);
    setNoteForm({
      title: note.title || '', description: note.description || '', status: note.status || 'todo',
      priority: note.priority || 'medium', category: note.category || 'other',
      assignedTo: note.assignedTo || '', dueDate: note.dueDate || '', dueTime: note.dueTime || ''
    });
    setShowAddNote(true);
  };

  return (
                <div className="space-y-6">

                  {/* ============ NOTES / TASK MANAGER ============ */}
                  <div className={`rounded-xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          Notes & Tasks
                        </h3>
                        <button
                          onClick={() => { setShowAddNote(true); setEditingNote(null); setNoteForm({ title: '', description: '', status: 'todo', priority: 'medium', category: 'other', assignedTo: '', dueDate: '', dueTime: '' }); }}
                          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          + Add Task
                        </button>
                      </div>

                      {/* Filters */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <select value={noteFilter.status} onChange={(e) => setNoteFilter({ ...noteFilter, status: e.target.value })} className={`px-2 py-1 text-xs border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
                          <option value="">All Status</option>
                          <option value="todo">Todo</option>
                          <option value="in_progress">In Progress</option>
                          <option value="done">Done</option>
                        </select>
                        <select value={noteFilter.priority} onChange={(e) => setNoteFilter({ ...noteFilter, priority: e.target.value })} className={`px-2 py-1 text-xs border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
                          <option value="">All Priority</option>
                          <option value="urgent">Urgent</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                          <option value="low">Low</option>
                        </select>
                        <select value={noteFilter.category} onChange={(e) => setNoteFilter({ ...noteFilter, category: e.target.value })} className={`px-2 py-1 text-xs border rounded-lg ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}>
                          <option value="">All Category</option>
                          <option value="feature">Feature</option>
                          <option value="bug">Bug</option>
                          <option value="glitch">Glitch</option>
                          <option value="content">Content</option>
                          <option value="other">Other</option>
                        </select>
                        {selectedCalendarDate && (
                          <button onClick={() => setSelectedCalendarDate(null)} className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500">
                            Clear date filter
                          </button>
                        )}
                      </div>

                      {/* Calendar Strip */}
                      <div className={`flex items-center gap-1 mb-4 p-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <button onClick={() => setCalendarWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; })} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>&lt;</button>
                        {Array.from({ length: 7 }, (_, i) => {
                          const day = new Date(calendarWeekStart);
                          day.setDate(day.getDate() + i);
                          const dateStr = day.toISOString().split('T')[0];
                          const isToday = new Date().toISOString().split('T')[0] === dateStr;
                          const isSelected = selectedCalendarDate === dateStr;
                          const count = adminNotes.filter(n => n.dueDate === dateStr && n.status !== 'done').length;
                          return (
                            <button
                              key={i}
                              onClick={() => setSelectedCalendarDate(isSelected ? null : dateStr)}
                              className={`flex-1 py-1.5 px-1 rounded-lg text-center transition-all ${
                                isSelected ? 'bg-blue-600 text-white shadow-md' :
                                isToday ? (isDarkMode ? 'bg-blue-900/40 text-blue-300 ring-1 ring-blue-500' : 'bg-blue-50 text-blue-700 ring-1 ring-blue-300') :
                                (isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-100 text-gray-600')
                              }`}
                            >
                              <div className="text-[10px] font-medium">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][day.getDay()]}</div>
                              <div className="text-sm font-bold">{day.getDate()}</div>
                              {count > 0 && <div className={`text-[10px] ${isSelected ? 'text-blue-200' : 'text-blue-500'}`}>{count}</div>}
                            </button>
                          );
                        })}
                        <button onClick={() => setCalendarWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; })} className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>&gt;</button>
                      </div>

                      {/* Add/Edit Form */}
                      {showAddNote && (
                        <div className={`mb-4 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                          <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {editingNote ? 'Edit Task' : 'New Task'}
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <input type="text" placeholder="Task title *" value={noteForm.title} onChange={(e) => setNoteForm({ ...noteForm, title: e.target.value })}
                              className={`sm:col-span-2 px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`} />
                            <textarea placeholder="Description (optional)" value={noteForm.description} onChange={(e) => setNoteForm({ ...noteForm, description: e.target.value })} rows={2}
                              className={`sm:col-span-2 px-3 py-2 text-sm border rounded-lg resize-none ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`} />
                            <select value={noteForm.priority} onChange={(e) => setNoteForm({ ...noteForm, priority: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                              <option value="low">Low Priority</option>
                              <option value="medium">Medium Priority</option>
                              <option value="high">High Priority</option>
                              <option value="urgent">Urgent</option>
                            </select>
                            <select value={noteForm.category} onChange={(e) => setNoteForm({ ...noteForm, category: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                              <option value="feature">Feature</option>
                              <option value="bug">Bug</option>
                              <option value="glitch">Glitch</option>
                              <option value="content">Content</option>
                              <option value="other">Other</option>
                            </select>
                            <input type="text" placeholder="Assigned to (name)" value={noteForm.assignedTo} onChange={(e) => setNoteForm({ ...noteForm, assignedTo: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`} />
                            <select value={noteForm.status} onChange={(e) => setNoteForm({ ...noteForm, status: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}>
                              <option value="todo">Todo</option>
                              <option value="in_progress">In Progress</option>
                              <option value="done">Done</option>
                            </select>
                            <input type="date" value={noteForm.dueDate} onChange={(e) => setNoteForm({ ...noteForm, dueDate: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                            <input type="time" value={noteForm.dueTime} onChange={(e) => setNoteForm({ ...noteForm, dueTime: e.target.value })}
                              className={`px-3 py-2 text-sm border rounded-lg ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`} />
                          </div>
                          <div className="flex gap-2 mt-3">
                            <button onClick={saveNote} className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors">
                              {editingNote ? 'Update' : 'Add Task'}
                            </button>
                            <button onClick={() => { setShowAddNote(false); setEditingNote(null); }} className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Active Tasks */}
                      <div className="space-y-2">
                        {adminNotes
                          .filter(n => n.status !== 'done')
                          .filter(n => !noteFilter.status || n.status === noteFilter.status)
                          .filter(n => !noteFilter.priority || n.priority === noteFilter.priority)
                          .filter(n => !noteFilter.category || n.category === noteFilter.category)
                          .filter(n => !selectedCalendarDate || n.dueDate === selectedCalendarDate)
                          .sort((a, b) => {
                            const p = { urgent: 0, high: 1, medium: 2, low: 3 };
                            return (p[a.priority] || 2) - (p[b.priority] || 2);
                          })
                          .map(note => (
                            <div key={note.id} className={`flex items-start gap-3 p-3 rounded-lg border-l-4 shadow-sm transition-all hover:shadow-md ${
                              note.priority === 'urgent' ? 'border-l-red-500' :
                              note.priority === 'high' ? 'border-l-orange-500' :
                              note.priority === 'medium' ? 'border-l-blue-500' : 'border-l-gray-400'
                            } ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'}`}
                            style={{ borderRightWidth: '1px', borderTopWidth: '1px', borderBottomWidth: '1px' }}>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                                  <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded uppercase ${
                                    note.priority === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                    note.priority === 'high' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' :
                                    note.priority === 'medium' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' :
                                    'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                                  }`}>{note.priority}</span>
                                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                    {note.category === 'bug' ? '🐛' : note.category === 'feature' ? '✨' : note.category === 'glitch' ? '⚠️' : note.category === 'content' ? '📄' : '📝'} {note.category}
                                  </span>
                                  {note.status === 'in_progress' && (
                                    <span className="px-1.5 py-0.5 text-[10px] rounded bg-blue-500/20 text-blue-600 dark:text-blue-300 animate-pulse font-medium">In Progress</span>
                                  )}
                                  {note.dueDate && (
                                    <span className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      Due: {new Date(note.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{note.dueTime ? `, ${note.dueTime}` : ''}
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{note.title}</p>
                                {note.description && <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{note.description}</p>}
                                {note.assignedTo && <p className={`text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Assigned to: {note.assignedTo}</p>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button onClick={() => toggleNoteStatus(note.id, note.status)} title="Mark done"
                                  className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors text-sm">✓</button>
                                <button onClick={() => startEditNote(note)} title="Edit"
                                  className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors text-sm">✏️</button>
                                <button onClick={() => deleteNote(note.id)} title="Delete"
                                  className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors text-sm">🗑️</button>
                              </div>
                            </div>
                          ))
                        }
                        {adminNotes.filter(n => n.status !== 'done').length === 0 && (
                          <p className={`text-center py-4 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No active tasks. Click "+ Add Task" to create one.</p>
                        )}
                      </div>

                      {/* Completed Tasks (collapsible) */}
                      {adminNotes.filter(n => n.status === 'done').length > 0 && (
                        <div className="mt-4">
                          <button onClick={() => setShowCompletedNotes(!showCompletedNotes)}
                            className={`w-full flex items-center justify-between py-2 px-3 rounded-lg text-sm transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-50 text-gray-500'}`}>
                            <span>Completed ({adminNotes.filter(n => n.status === 'done').length})</span>
                            <span className={`transition-transform ${showCompletedNotes ? 'rotate-180' : ''}`}>▼</span>
                          </button>
                          {showCompletedNotes && (
                            <div className="space-y-1 mt-2">
                              {adminNotes.filter(n => n.status === 'done').map(note => (
                                <div key={note.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                                  <span className="text-green-500 text-sm">✅</span>
                                  <span className={`flex-1 text-sm line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{note.title}</span>
                                  {note.completedAt && <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{new Date(note.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                                  {note.assignedTo && <span className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{note.assignedTo}</span>}
                                  <button onClick={() => toggleNoteStatus(note.id, note.status)} title="Reopen" className="text-[10px] text-blue-500 hover:text-blue-600">Undo</button>
                                  <button onClick={() => deleteNote(note.id)} title="Delete" className="text-[10px] text-red-400 hover:text-red-500">🗑️</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    Analytics Overview
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Total Translations
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.translations || 0}
                          </p>
                        </div>
                        <div className="text-blue-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Verified
                          </p>
                          <p className={`text-2xl font-bold text-green-600`}>
                            {analytics.totals?.verified || 0}
                          </p>
                        </div>
                        <div className="text-green-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Auto-Generated
                          </p>
                          <p className={`text-2xl font-bold text-yellow-600`}>
                            {analytics.totals?.auto || 0}
                          </p>
                        </div>
                        <div className="text-yellow-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Requests Today
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.requests_today || 0}
                          </p>
                        </div>
                        <div className="text-purple-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            This Week
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.requests_this_week || 0}
                          </p>
                        </div>
                        <div className="text-indigo-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            This Month
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.requests_this_month || 0}
                          </p>
                        </div>
                        <div className="text-pink-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Active Users Today
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.active_users_today || 0}
                          </p>
                        </div>
                        <div className="text-teal-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className={`p-4 rounded-lg border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Word Suggestions
                          </p>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {analytics.totals?.word_suggestions || 0}
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {analytics.totals?.pending_suggestions || 0} pending
                          </p>
                        </div>
                        <div className="text-orange-500">
                          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Most Searched Terms */}
                  {analytics.most_searched && analytics.most_searched.length > 0 && (
                    <div className={`rounded-lg border p-4 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Most Searched Terms
                      </h4>
                      <div className="space-y-2">
                        {analytics.most_searched.slice(0, 5).map((term, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {term._id}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded ${
                              isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {term.count} searches
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

  );
};

export default DashboardAdmin;
